/*
 * Testimonia — "Sube tus fotos" (snippets/testimonia-photo-upload.liquid)
 *
 * Keeps the list of chosen photos, grades each one by its pixel size, and owns the add-to-cart
 * while photos are present: one request per photo on the same cart line, because Shopify refuses
 * any cart request over about 20 MB. The snippet's comment explains the sequence and its limits.
 */
(function () {
  if (customElements.get('testimonia-photo-upload')) return;

  var PREVIEWABLE = /^image\/(jpeg|png|webp|gif)$/;
  var ACCEPTED = /\.(jpe?g|png|tiff?|heic)$/i;

  function formatMb(bytes) {
    return (bytes / (1024 * 1024)).toFixed(1).replace('.', ',') + ' MB';
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Resolves with the parsed JSON, or rejects with Shopify's own message when it sends one.
  function post(path, body) {
    var init = { method: 'POST', headers: { Accept: 'application/json' }, body: body };
    if (!(body instanceof FormData)) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    return fetch(cartRoot() + path, init).then(function (r) {
      if (r.status === 413) throw new Error('Una de las fotos es demasiado pesada para subirla.');
      return r.json().then(function (json) {
        if (!r.ok || json.status) throw new Error(json.description || json.message || 'No pudimos agregar el producto.');
        return json;
      });
    });
  }

  function readSize(file) {
    return new Promise(function (resolve) {
      if (!PREVIEWABLE.test(file.type)) return resolve(null);
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        resolve({ url: url, width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        resolve(false);
      };
      img.src = url;
    });
  }

  class TestimoniaPhotoUpload extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-tpu-input]');
      if (!this.input || this.input.dataset.tpuBound) return;
      this.input.dataset.tpuBound = '1';

      this.zone = this.querySelector('[data-tpu-zone]');
      this.list = this.querySelector('[data-tpu-list]');
      this.status = this.querySelector('[data-tpu-status]');
      this.label = this.dataset.label || 'Foto';
      this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 6;
      this.maxBytes = (parseFloat(this.dataset.maxMb) || 18) * 1024 * 1024;
      this.minPx = parseInt(this.dataset.minPx, 10) || 0;
      this.goodPx = parseInt(this.dataset.goodPx, 10) || 0;
      this.photos = [];

      this.bindForm();
      this.bindDrop();
      this.input.addEventListener('change', this.onPick.bind(this));
      this.input.addEventListener('invalid', this.onInvalid.bind(this));
      this.list.addEventListener('click', this.onRemove.bind(this));
      this.render();
    }

    bindForm() {
      // The installment (Shop Pay) form also posts to /cart/add and comes first in the DOM.
      var section = this.closest('.product-section') || document;
      this.form =
        section.querySelector('form[data-type="add-to-cart-form"][id]') ||
        section.querySelector('form[action*="/cart/add"][id]:not(.installment)');
      if (!this.form) return;

      // getAttribute, not form.id: the product form holds an input named "id", which shadows it.
      // The `form` attribute only makes `required` count; the picker has no name, so the files
      // never ride along with a native submit.
      this.input.setAttribute('form', this.form.getAttribute('id'));

      // Capture on the form's parent: after testimonia-gift-wrap's document-level pass, before
      // product-form.js's own listener on the form.
      (this.form.parentElement || this.form).addEventListener('submit', this.onSubmit.bind(this), true);
    }

    bindDrop() {
      var self = this;
      ['dragenter', 'dragover'].forEach(function (type) {
        self.zone.addEventListener(type, function (e) {
          e.preventDefault();
          self.zone.classList.add('is-dragover');
        });
      });
      ['dragleave', 'drop'].forEach(function (type) {
        self.zone.addEventListener(type, function () {
          self.zone.classList.remove('is-dragover');
        });
      });
      this.zone.addEventListener('drop', function (e) {
        e.preventDefault();
        if (e.dataTransfer && e.dataTransfer.files.length) self.addFiles(e.dataTransfer.files);
      });
    }

    onPick() {
      var files = Array.prototype.slice.call(this.input.files);
      // Emptied so picking the same file again still fires `change`.
      this.input.value = '';
      this.addFiles(files);
    }

    onInvalid(e) {
      // Our own message instead of the browser bubble, which points at an input nobody sees.
      e.preventDefault();
      this.setStatus('Sube al menos una foto para continuar.', 'error');
      this.zone.classList.add('is-error');
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    onRemove(e) {
      var button = e.target.closest('[data-tpu-remove]');
      if (!button || this.busy) return;
      var photo = this.photos.splice(parseInt(button.dataset.tpuRemove, 10), 1)[0];
      if (photo && photo.url) URL.revokeObjectURL(photo.url);
      this.setStatus('', '');
      this.render();
    }

    addFiles(fileList) {
      var self = this;
      var files = Array.prototype.slice.call(fileList);
      var refused = [];
      var room = this.maxFiles - this.photos.length;

      if (files.length > room) {
        var extra = files.length - room;
        refused.push('Puedes subir hasta ' + this.maxFiles + ' fotos; ' + extra + (extra === 1 ? ' no se agregó.' : ' no se agregaron.'));
        files = files.slice(0, Math.max(room, 0));
      }

      files = files.filter(function (file) {
        if (!PREVIEWABLE.test(file.type) && !ACCEPTED.test(file.name)) {
          refused.push(file.name + ': formato no admitido.');
          return false;
        }
        if (file.size > self.maxBytes) {
          refused.push(file.name + ' pesa ' + formatMb(file.size) + '; el máximo es ' + formatMb(self.maxBytes) + '.');
          return false;
        }
        return true;
      });

      Promise.all(files.map(readSize)).then(function (sizes) {
        sizes.forEach(function (size, i) {
          var file = files[i];
          if (size === false) return refused.push(file.name + ': no pudimos leer la imagen.');
          var photo = { file: file, grade: 'unread' };
          if (size) {
            photo.url = size.url;
            photo.width = size.width;
            photo.height = size.height;
            var shortest = Math.min(size.width, size.height);
            if (shortest < self.minPx) {
              URL.revokeObjectURL(size.url);
              return refused.push(file.name + ': resolución muy baja (' + shortest + ' px en el lado menor, mínimo ' + self.minPx + ').');
            }
            photo.grade = shortest < self.goodPx ? 'good' : 'high';
          }
          self.photos.push(photo);
        });
        self.render();
        if (refused.length) {
          self.setStatus(refused.join(' '), 'error');
        } else if (self.photos.some(function (p) { return p.grade === 'good'; })) {
          self.setStatus('Las fotos marcadas «Buena calidad» funcionan en tamaños pequeños y medianos. Para formatos grandes recomendamos más resolución.', 'warn');
        } else {
          self.setStatus(self.photos.length ? 'Tus fotos están listas para imprimir.' : '', 'ok');
        }
      });
    }

    render() {
      var self = this;
      this.list.innerHTML = '';
      this.photos.forEach(function (photo, i) {
        var item = document.createElement('li');
        item.className = 'testimonia-photo-upload__item';

        var thumb = document.createElement(photo.url ? 'img' : 'span');
        thumb.className = 'testimonia-photo-upload__thumb';
        if (photo.url) {
          thumb.src = photo.url;
          thumb.alt = '';
          thumb.width = 56;
          thumb.height = 56;
        }

        var meta = document.createElement('span');
        meta.className = 'testimonia-photo-upload__meta';
        var name = document.createElement('span');
        name.className = 'testimonia-photo-upload__name';
        name.textContent = self.label + ' ' + (i + 1) + ' · ' + photo.file.name;
        var details = document.createElement('span');
        details.className = 'testimonia-photo-upload__details';
        details.textContent = (photo.width ? photo.width + ' × ' + photo.height + ' px · ' : '') + formatMb(photo.file.size);
        var grade = document.createElement('span');
        grade.className = 'testimonia-photo-upload__grade';
        grade.dataset.grade = photo.grade;
        grade.textContent = { high: 'Alta calidad', good: 'Buena calidad', unread: 'Sin vista previa' }[photo.grade];
        meta.append(name, details, grade);

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'testimonia-photo-upload__remove';
        remove.dataset.tpuRemove = i;
        remove.setAttribute('aria-label', 'Quitar ' + photo.file.name);
        remove.textContent = '×';

        item.append(thumb, meta, remove);
        self.list.appendChild(item);
      });

      var count = this.photos.length;
      var full = count >= this.maxFiles;
      this.list.hidden = !count;
      this.input.required = !count;
      this.input.disabled = full;
      this.zone.classList.toggle('is-full', full);
      this.zone.classList.remove('is-error');
      this.querySelector('[data-tpu-cta]').textContent = full
        ? 'Llegaste al máximo de fotos'
        : count ? 'Agregar más fotos' : 'Elige tus fotos o arrástralas aquí';
      this.querySelector('[data-tpu-count]').textContent = count ? count + ' de ' + this.maxFiles + ' fotos' : '';
    }

    onSubmit(e) {
      if (e.target !== this.form || !this.photos.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!this.busy) this.upload();
    }

    propName(i) {
      return 'properties[' + this.label + ' ' + (i + 1) + ']';
    }

    upload() {
      var self = this;
      var photos = this.photos.slice();
      var total = photos.length;
      var line = null;
      var firstKey = this.label + ' 1';

      this.setBusy(true);
      this.progress(1, total);

      var body = new FormData(this.form);
      body.append(this.propName(0), photos[0].file);

      var chain = post('cart/add.js', body).then(function (added) {
        line = added;
      });

      photos.slice(1).forEach(function (photo, n) {
        chain = chain.then(function () {
          self.progress(n + 2, total);
          var next = new FormData();
          next.append('id', line.key);
          next.append('quantity', line.quantity);
          Object.keys(line.properties || {}).forEach(function (key) {
            if (line.properties[key] != null) next.append('properties[' + key + ']', line.properties[key]);
          });
          next.append(self.propName(n + 1), photo.file);
          return post('cart/change.js', next).then(function (cart) {
            var url = line.properties[firstKey];
            line = cart.items.filter(function (item) {
              return item.properties && item.properties[firstKey] === url;
            })[0];
            if (!line) throw new Error('No pudimos guardar todas tus fotos.');
          });
        });
      });

      chain
        .then(function () {
          self.setStatus('Fotos subidas. Producto agregado al carro.', 'ok');
          return self.refreshCart(line);
        })
        .catch(function (err) {
          console.error('[TPU] Upload error:', err);
          // Never leave a line in the cart with only some of the photos.
          var undo = line ? post('cart/change.js', { id: line.key, quantity: 0 }).catch(function () {}) : Promise.resolve();
          return undo.then(function () {
            self.setStatus(err.message + ' Inténtalo de nuevo.', 'error');
          });
        })
        .then(function () {
          self.setBusy(false);
        });
    }

    progress(n, total) {
      this.setStatus(total > 1 ? 'Subiendo foto ' + n + ' de ' + total + '…' : 'Subiendo tu foto…', 'busy');
    }

    refreshCart(line) {
      var cartUrl = (window.routes && window.routes.cart_url) || cartRoot() + 'cart';
      var drawer = document.querySelector('cart-drawer');
      var notification = document.querySelector('cart-notification');

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'testimonia-photo-upload' });
      }

      var renderer = drawer && typeof drawer.renderContents === 'function' ? drawer
        : notification && typeof notification.renderContents === 'function' ? notification
        : null;
      if (!renderer) {
        window.location.href = cartUrl;
        return;
      }

      var sections = renderer.getSectionsToRender().map(function (s) { return s.section || s.id; });
      return fetch(cartUrl + '?sections=' + sections.join(','))
        .then(function (r) { return r.json(); })
        .then(function (json) {
          renderer.renderContents(Object.assign({}, line, { sections: json }));
        })
        .catch(function () {
          window.location.href = cartUrl;
        });
    }

    setBusy(busy) {
      this.busy = busy;
      this.classList.toggle('is-busy', busy);
      var button = this.form && this.form.querySelector('[type="submit"]');
      if (!button) return;
      button.classList.toggle('loading', busy);
      if (busy) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
      var spinner = button.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.toggle('hidden', !busy);
    }

    setStatus(message, tone) {
      this.status.textContent = message || '';
      this.status.dataset.tone = tone || '';
    }
  }

  customElements.define('testimonia-photo-upload', TestimoniaPhotoUpload);
})();
